/** The vertical extent of the page the reader can see, in
 * `getBoundingClientRect` coordinates. */
export interface VisibleViewportBand {
  top: number;
  bottom: number;
}

/**
 * The band of the page the reader can actually see. A soft keyboard shrinks
 * `visualViewport` and leaves `window.innerHeight` alone, so a floating layer
 * that decides open-above-or-below, or how tall it may grow, against
 * `innerHeight` hangs behind the keyboard. `visualViewport.offsetTop` is
 * measured from the layout viewport's top, the same origin
 * `getBoundingClientRect` uses, so the band compares directly with an anchor's
 * rect. Where there is no `visualViewport` the band is the whole window.
 *
 * Only the DECISION and the height cap read the band. A fixed layer's CSS
 * anchoring (`top`, or `bottom` measured from the layout viewport's bottom
 * edge) still reads the window, because that is the box the browser positions
 * `position: fixed` against.
 */
export function visibleViewportBand(): VisibleViewportBand {
  const viewport = window.visualViewport ?? null;
  if (viewport === null) return { top: 0, bottom: window.innerHeight };
  return { top: viewport.offsetTop, bottom: viewport.offsetTop + viewport.height };
}

/**
 * Calls `onChange` whenever the visible band moves: the keyboard opening or
 * closing, or the visual viewport panning to reveal a focused control. Neither
 * fires `window` `resize` on a phone, so a layer that measured when it opened
 * would keep the band it opened against. Returns the unsubscribe; where there
 * is no `visualViewport` there is nothing to subscribe to and it does nothing.
 */
export function subscribeVisibleViewport(onChange: () => void): () => void {
  const viewport = window.visualViewport ?? null;
  if (viewport === null) return () => undefined;
  viewport.addEventListener("resize", onChange);
  viewport.addEventListener("scroll", onChange);
  return () => {
    viewport.removeEventListener("resize", onChange);
    viewport.removeEventListener("scroll", onChange);
  };
}
